import type { EquipmentFace, SimpleLayout, MultiFaceLayout, PortKind } from './index';

export type PoEStandard = '802.3af' | '802.3at' | '802.3bt' | 'passive-24' | 'passive-48';

export type PowerInputKind = 'ac' | 'dc-jack' | 'dc-terminal' | 'poe-in' | 'pv' | 'battery';

export type PowerOutputKind = 'ac' | 'dc';

export type DevicePowerClass = 'none' | 'solar' | 'battery' | 'inverter' | 'psu';

/** How the device chooses among live supplies. Default is highest voltage (MikroTik-style). */
export type PowerSelectMode = 'highest-voltage' | 'listed-order';

/** Power-input rating is entered as watts or amps — never both. */
export type PowerCapacityLimit = 'watts' | 'amps';

export type LogicMode = 'plan' | 'logic';

export type LogicIssueCode =
  | 'UNPOWERED'
  | 'MISSING_PSU'
  | 'POE_INCOMPATIBLE'
  | 'BUDGET_EXCEEDED'
  | 'FORCED_INVALID'
  | 'VOLTAGE_DAMAGE'
  | 'MEDIA_MISMATCH'
  | 'SPEED_MISMATCH'
  | 'CABLE_TYPE'
  | 'PASSTHROUGH_OPEN'
  | 'UNMODELED'
  | 'COMBO_CONFLICT'
  | 'DANGLING_CABLE'
  | 'EXCLUDED_NEIGHBOR'
  | 'LAYOUT_LOGIC_MISMATCH'
  | 'UNSTABLE'
  | 'PV_POLARITY'
  | 'PV_OPEN_STRING'
  | 'PV_VOC_EXCEEDED'
  | 'PV_MPPT_RANGE'
  | 'PV_POWER_EXCEEDED'
  | 'PV_ISC_EXCEEDED'
  | 'PV_UNSOLVABLE'
  | 'BAT_POLARITY'
  | 'BACKUP_VOLTAGE_MISMATCH'
  | 'BACKUP_NO_STORAGE'
  | 'INVERTER_OVERLOAD'
  | 'POWER_PORT_ROLE';

export type LogicIssueCategory = 'power' | 'poe' | 'physical' | 'ports' | 'other';

export type LogicSeverity = 'error' | 'warning' | 'info';

export interface PowerInput {
  id: string;
  kind: PowerInputKind;
  /** Bound layout port for poe-in (and optional DC/AC jack ports). */
  portNumber?: number;
  face?: EquipmentFace;
  /** Inclusive range. Equal min/max is a single rail (e.g. 12V). */
  voltageMin?: number;
  voltageMax?: number;
  maxWatts?: number;
  maxAmps?: number;
  /** Which rating the user entered. Default `watts` when omitted (catalog SKUs). */
  capacityLimit?: PowerCapacityLimit;
  /** Default true. Disabled inputs stay on the model but are ignored by the solver. */
  enabled?: boolean;
  /** Groups a PV+ / PV− layout pair (or BAT+ / BAT−) into one MPPT / battery input. */
  mpptId?: string;
  plusPortNumber?: number;
  minusPortNumber?: number;
  plusFace?: EquipmentFace;
  minusFace?: EquipmentFace;
  mpptVoltageMin?: number;
  mpptVoltageMax?: number;
  maxIsc?: number;
  startupVoltage?: number;
}

export interface PowerOutput {
  id: string;
  kind: PowerOutputKind;
  portNumber?: number;
  face?: EquipmentFace;
  voltageMin?: number;
  voltageMax?: number;
  maxWatts?: number;
  maxAmps?: number;
  capacityLimit?: PowerCapacityLimit;
  enabled?: boolean;
}

export interface PoEInSpec {
  standards: PoEStandard[];
  voltageMin: number;
  voltageMax: number;
  maxWatts?: number;
}

export interface PoEOutSpec {
  standards: PoEStandard[];
  perPortMaxWatts: number;
  modeDefault?: 'auto' | 'off';
  outputFollowsInputVoltage?: boolean;
  poeOutOnlyWhenPoweredBy?: readonly PowerInputKind[];
  requireInputVoltageIn?: [number, number];
}

export type PortRoleKind = 'poe-in' | 'poe-out';

/** A PoE spec shared by one or more ports. Specs are also copied onto each assigned port for the solver. */
export interface DevicePortRole {
  id: string;
  kind: PortRoleKind;
  name: string;
  poeIn?: PoEInSpec;
  poeOut?: PoEOutSpec;
  /** Default true. Disabled roles stay on the graph but Logic ignores their PoE. */
  enabled?: boolean;
}

export type ElectricalGraphPinSide = 'left' | 'right' | 'top' | 'bottom';

export interface ElectricalGraphWireEnds {
  from?: ElectricalGraphPinSide;
  to?: ElectricalGraphPinSide;
}

export interface ElectricalGraphCamera {
  x: number;
  y: number;
  zoom: number;
}

export interface ElectricalGraphPos {
  x: number;
  y: number;
}

/** Blueprint presentation only. Bindings live on inputs/outputs/roles/ports. */
export interface ElectricalGraph {
  camera?: ElectricalGraphCamera;
  boards?: Partial<Record<EquipmentFace, ElectricalGraphPos>>;
  cards?: Record<string, ElectricalGraphPos>;
  /** Chosen pip sides for reconstructed wires (`fromId->toId`). */
  wireEnds?: Record<string, ElectricalGraphWireEnds>;
}

export interface PortLogicFields {
  speedsMbps?: number[];
  poeIn?: PoEInSpec;
  poeOut?: PoEOutSpec;
  poeInRoleId?: string;
  poeOutRoleId?: string;
  passthrough?: boolean;
  comboGroup?: string;
}

export interface PoweredByConfig {
  /** PoE from the ethernet path (PD). */
  kind: 'poe' | 'assigned-psu' | 'cabled-psu' | 'ac-assumed';
  assignedPsuId?: string;
}

export interface PortInstanceLogic {
  poeMode?: 'auto' | 'forced' | 'off';
  forcedStandard?: PoEStandard;
}

export interface DeviceModel {
  equipmentId: string;
  /** Idle watts when `selfDrawLimit` is `watts`. Also cached W = V × A when the user entered amps. */
  selfDrawWatts: number;
  /** Idle current when `selfDrawLimit` is `amps`. Solver uses W = V × A at the winning input. */
  selfDrawAmps?: number;
  /** Which self-draw rating the user entered. Default `watts` when omitted (catalog SKUs). */
  selfDrawLimit?: PowerCapacityLimit;
  powerInputs: PowerInput[];
  /** Default `highest-voltage`. `listed-order` walks `powerInputs` top to bottom. */
  powerSelect?: PowerSelectMode;
  layout: {
    simple?: SimpleLayout;
    multiFace?: MultiFaceLayout;
    numberingSchemeId?: string;
  };
  /** Named PoE roles. Optional; the form synthesizes these from port specs when missing. */
  portRoles?: DevicePortRole[];
  /** Catalog brick this SKU ships with (optional assigned default). */
  shipsWithPsuId?: string;
  isPsu?: boolean;
  psuOutput?: { voltage: number; watts: number };
  isInjector?: boolean;
  /** Patch panel / coupler — no local power required. */
  isPassthroughDevice?: boolean;
  powerOutputs?: PowerOutput[];
  isSolar?: boolean;
  isBattery?: boolean;
  isInverter?: boolean;
  /** Always-on AC source (wall outlet). Supplies AC out whenever included in Logic. */
  isMainsOutlet?: boolean;
  /**
   * When true (default for inverters), the unit will not start / supply AC without a battery bank
   * on BAT+/BAT−.
   */
  requiresBatteryToStart?: boolean;
  /** Module STC (solar). */
  pvVoc?: number;
  pvVmp?: number;
  pvIsc?: number;
  pvImp?: number;
  pvStcWatts?: number;
  capacityAh?: number;
  nominalVoltage?: number;
  usableFraction?: number;
  inverterEfficiency?: number;
  maxDischargeAmps?: number;
  electricalGraph?: ElectricalGraph;
}

export interface LogicIssue {
  id: string;
  code: LogicIssueCode;
  severity: LogicSeverity;
  category: LogicIssueCategory;
  message: string;
  details?: string;
  equipmentId?: string;
  rackId?: string;
  instanceId?: string;
  portNumber?: number;
  portKind?: PortKind | string;
  face?: string;
  cableId?: string;
  idle?: boolean;
}

export interface DeviceLogicState {
  nodeId: string;
  equipmentId: string;
  name: string;
  catalogId: string;
  type: string;
  excluded: boolean;
  /** Switched off — excluded from Logic and never supplies power. */
  poweredOff?: boolean;
  unmodeled: boolean;
  idle: boolean;
  powered: boolean;
  inTopology: boolean;
  inputVoltage?: number;
  inputWatts?: number;
  /** Actual draw while powered; 0 when unpowered/excluded. */
  selfDrawWatts: number;
  /** Model rating used for “needs X W” messaging when unpowered. */
  ratedSelfDrawWatts?: number;
  poeBudgetWatts?: number;
  poeUsedWatts?: number;
  /** True when this device can source PoE-out while powered. */
  isPse?: boolean;
  winningInputKind?: PowerInputKind | 'assigned-psu' | 'cabled-psu' | 'ac';
}

export interface PortLogicState {
  nodeId: string;
  portNumber: number;
  kind: string;
  face?: string;
  offeredStandards: PoEStandard[];
  negotiated?: {
    standard: PoEStandard;
    watts: number;
  } | null;
  caption: string;
}

export type CableLogicStatus = 'ok' | 'warning' | 'error' | 'excluded';

export interface CableLogicState {
  cableId: string;
  status: CableLogicStatus;
}

export interface LogicOverview {
  devicesInLogic: number;
  powered: number;
  unpowered: number;
  excluded: number;
  idle: number;
  poeUsedWatts: number;
  poeBudgetWatts: number;
  cablesOk: number;
  cablesFlagged: number;
  missingPsu: number;
}

export interface BackupPowerPvTracker {
  id: string;
  label: string;
  /** String Vmp at the MPPT (0 if nothing connected). */
  arrayVolts: number;
  /** Inverter MPPT minimum operating voltage. */
  mpptMinVolts: number | null;
  mpptMaxVolts: number | null;
}

export interface BackupPowerIsland {
  inverterId: string;
  inverterName: string;
  batteryVolts: number;
  batteryAh: number;
  usableWh: number;
  solarStcWatts: number;
  pvUsedWatts: number;
  pvMaxWatts: number;
  pvTrackers: BackupPowerPvTracker[];
  loadWatts: number;
  inverterAcWatts: number;
  efficiency: number;
  overnightHours: number | null;
}

export interface BackupPowerSummary {
  islands: BackupPowerIsland[];
  solarStcWatts: number;
  usableWh: number;
  loadWatts: number;
  overnightHours: number | null;
}

export interface LogicResult {
  issues: LogicIssue[];
  devices: Record<string, DeviceLogicState>;
  ports: Record<string, PortLogicState>;
  cables: Record<string, CableLogicState>;
  overview: LogicOverview;
  backupPower?: BackupPowerSummary;
}

export const POE_STANDARDS: PoEStandard[] = [
  '802.3af',
  '802.3at',
  '802.3bt',
  'passive-24',
  'passive-48',
];

export const POE_STANDARD_LABEL: Record<PoEStandard, string> = {
  '802.3af': '802.3af',
  '802.3at': '802.3at',
  '802.3bt': '802.3bt',
  'passive-24': '24V passive',
  'passive-48': '48V passive',
};

export const POE_NOMINAL_VOLTAGE: Record<PoEStandard, number> = {
  '802.3af': 48,
  '802.3at': 48,
  '802.3bt': 52,
  'passive-24': 24,
  'passive-48': 48,
};

export const POE_DEFAULT_WATTS: Record<PoEStandard, number> = {
  '802.3af': 15.4,
  '802.3at': 30,
  '802.3bt': 60,
  'passive-24': 12,
  'passive-48': 24,
};

export const ENDPOINT_TYPES_DEFAULT_EXCLUDED = new Set([
  'computer',
  'laptop',
  'printer',
  'tv',
  'game-console',
  'media-player',
  'generic-node',
]);

export function portLogicKey(face: string | undefined, number: number, kind?: string): string {
  const f = face || 'front';
  return kind ? `${f}:${number}:${kind}` : `${f}:${number}`;
}

export function isFiberKind(kind?: string | null): boolean {
  const k = String(kind || '').toLowerCase();
  return k === 'sfp' || k === 'sfpplus' || k === 'sfp+' || k === 'qsfp' || k === 'fiber';
}

export function isCopperKind(kind?: string | null): boolean {
  const k = String(kind || '').toLowerCase();
  return k === 'rj45' || k === 'ethernet';
}

export function isPowerKind(kind?: string | null): boolean {
  const k = String(kind || '').toLowerCase();
  return k === 'ac' || k === 'dc' || k === 'pvplus' || k === 'pvminus' || k === 'batplus' || k === 'batminus';
}

export function isPvPortKind(kind?: string | null): boolean {
  const k = String(kind || '').toLowerCase();
  return k === 'pvplus' || k === 'pvminus';
}

export function isBatPortKind(kind?: string | null): boolean {
  const k = String(kind || '').toLowerCase();
  return k === 'batplus' || k === 'batminus';
}

export function powerPortFamily(kind?: string | null): 'pv' | 'bat' | 'ac' | 'dc' | null {
  const k = normalizePortKind(kind);
  if (k === 'pvPlus' || k === 'pvMinus') return 'pv';
  if (k === 'batPlus' || k === 'batMinus') return 'bat';
  if (k === 'ac') return 'ac';
  if (k === 'dc') return 'dc';
  return null;
}

export function isPositivePowerPole(kind?: string | null): boolean {
  const k = normalizePortKind(kind);
  return k === 'pvPlus' || k === 'batPlus';
}

export function isNegativePowerPole(kind?: string | null): boolean {
  const k = normalizePortKind(kind);
  return k === 'pvMinus' || k === 'batMinus';
}

export function powerCablesCompatible(a?: string | null, b?: string | null): boolean {
  const fa = powerPortFamily(a);
  const fb = powerPortFamily(b);
  if (!fa || !fb) return false;
  if (fa === 'pv' && fb === 'pv') return true;
  if (fa === 'bat' && fb === 'bat') return true;
  if (fa === 'dc' && fb === 'dc') return true;
  if (fa === 'ac' && fb === 'ac') return true;
  return false;
}

export function clampUnitInterval(n: number | undefined, fallback: number): number {
  if (n == null || !Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(1, n);
}

export function devicePowerClass(model: Pick<DeviceModel, 'isSolar' | 'isBattery' | 'isInverter' | 'isPsu'>): DevicePowerClass {
  if (model.isSolar) return 'solar';
  if (model.isBattery) return 'battery';
  if (model.isInverter) return 'inverter';
  if (model.isPsu) return 'psu';
  return 'none';
}

/** Inverters require a battery to start unless explicitly opted out. */
export function inverterRequiresBatteryToStart(
  model: Pick<DeviceModel, 'isInverter' | 'requiresBatteryToStart'>
): boolean {
  if (!model.isInverter) return false;
  return model.requiresBatteryToStart !== false;
}

export function derivedPsuOutput(model: DeviceModel): { voltage: number; watts: number } | undefined {
  const dcOut = (model.powerOutputs || []).find((o) => o.kind === 'dc' && o.enabled !== false);
  if (dcOut) {
    const voltage = dcOut.voltageMin != null && dcOut.voltageMax != null
      ? (dcOut.voltageMin + dcOut.voltageMax) / 2
      : dcOut.voltageMin ?? dcOut.voltageMax ?? 0;
    const watts = dcOut.maxWatts ?? 0;
    if (voltage > 0 && watts > 0) return { voltage, watts };
  }
  if (model.psuOutput && model.psuOutput.voltage > 0 && model.psuOutput.watts > 0) return model.psuOutput;
  return undefined;
}

/** Explicit AC outlet rating (inverter / UPS / PDU). */
export function derivedAcOutput(model: DeviceModel): { voltage: number; watts: number } | undefined {
  const acOut = (model.powerOutputs || []).find((o) => o.kind === 'ac' && o.enabled !== false);
  if (!acOut) return undefined;
  const voltage =
    acOut.voltageMin != null && acOut.voltageMax != null
      ? (acOut.voltageMin + acOut.voltageMax) / 2
      : acOut.voltageMin ?? acOut.voltageMax ?? 230;
  const watts = acOut.maxWatts ?? 0;
  if (!(watts > 0)) return undefined;
  return { voltage: voltage > 0 ? voltage : 230, watts };
}

export function normalizePortKind(kind?: string | null): string {
  if (!kind) return 'rj45';
  if (kind === 'sfp+') return 'sfpPlus';
  return kind;
}

export function isPowerInputEnabled(input: PowerInput): boolean {
  return input.enabled !== false;
}

export function activePowerInputs(inputs: PowerInput[] | undefined): PowerInput[] {
  return (inputs || []).filter(isPowerInputEnabled);
}

export function typicalVoltageOfInput(input: PowerInput): number | undefined {
  if (input.voltageMin != null && input.voltageMax != null) {
    return input.voltageMin === input.voltageMax ? input.voltageMin : (input.voltageMin + input.voltageMax) / 2;
  }
  if (input.voltageMin != null) return input.voltageMin;
  if (input.voltageMax != null) return input.voltageMax;
  if (input.kind === 'ac') return 230;
  if (input.kind === 'poe-in') return 48;
  return undefined;
}

export function inputVoltageSpan(input: PowerInput): { min: number; max: number } | null {
  const typical = typicalVoltageOfInput(input);
  const min = input.voltageMin ?? typical;
  const max = input.voltageMax ?? min;
  if (min == null || max == null) return null;
  return { min, max };
}

function roundWatts(n: number): number {
  return Math.round(n * 10) / 10;
}

function roundAmps(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Which rating is authoritative. Amps if that was chosen (or only amps are stored). */
export function inputCapacityLimit(input: Pick<PowerInput, 'capacityLimit' | 'maxAmps' | 'maxWatts'> | Pick<PowerOutput, 'capacityLimit' | 'maxAmps' | 'maxWatts'>): PowerCapacityLimit {
  if (input.capacityLimit === 'amps' || input.capacityLimit === 'watts') return input.capacityLimit;
  if (input.maxAmps != null && input.maxWatts == null) return 'amps';
  return 'watts';
}

/** Supply ceiling at a voltage. Watts mode uses maxWatts; amps mode uses W = V × A. */
export function inputCapacityWatts(input: PowerInput, voltage?: number): number | undefined {
  if (inputCapacityLimit(input) === 'amps') {
    const v = voltage ?? typicalVoltageOfInput(input);
    if (input.maxAmps != null && Number.isFinite(input.maxAmps) && input.maxAmps >= 0 && v != null && v > 0) {
      return roundWatts(input.maxAmps * v);
    }
    return undefined;
  }
  if (input.maxWatts != null && Number.isFinite(input.maxWatts) && input.maxWatts >= 0) return input.maxWatts;
  return undefined;
}

export function inputCapacityAmps(input: PowerInput, voltage?: number): number | undefined {
  if (inputCapacityLimit(input) === 'amps') {
    return input.maxAmps != null && Number.isFinite(input.maxAmps) && input.maxAmps >= 0 ? input.maxAmps : undefined;
  }
  const v = voltage ?? typicalVoltageOfInput(input);
  if (input.maxWatts != null && Number.isFinite(input.maxWatts) && input.maxWatts >= 0 && v != null && v > 0) {
    return roundAmps(input.maxWatts / v);
  }
  return undefined;
}

export function typicalDeviceVoltage(model: Pick<DeviceModel, 'powerInputs'>): number | undefined {
  const first = activePowerInputs(model.powerInputs)[0];
  return first ? typicalVoltageOfInput(first) : undefined;
}

/** Which self-draw rating is authoritative. Amps if that was chosen (or only amps are stored). */
export function selfDrawLimitOf(
  model: Pick<DeviceModel, 'selfDrawLimit' | 'selfDrawAmps' | 'selfDrawWatts'>
): PowerCapacityLimit {
  if (model.selfDrawLimit === 'amps' || model.selfDrawLimit === 'watts') return model.selfDrawLimit;
  if (model.selfDrawAmps != null) return 'amps';
  return 'watts';
}

export function selfDrawWattsAtVoltage(
  model: Pick<DeviceModel, 'selfDrawAmps' | 'selfDrawWatts' | 'selfDrawLimit' | 'powerInputs'>,
  voltage?: number
): number {
  if (selfDrawLimitOf(model) === 'amps') {
    const amps = model.selfDrawAmps;
    if (amps != null && Number.isFinite(amps) && amps >= 0) {
      const v = voltage ?? typicalDeviceVoltage(model);
      if (v != null && v > 0) return roundWatts(amps * v);
    }
    return 0;
  }
  return model.selfDrawWatts || 0;
}

export function deriveSelfDrawAmps(
  model: Pick<DeviceModel, 'selfDrawAmps' | 'selfDrawWatts' | 'selfDrawLimit' | 'powerInputs'>
): number {
  if (selfDrawLimitOf(model) === 'amps' && model.selfDrawAmps != null && Number.isFinite(model.selfDrawAmps)) {
    return model.selfDrawAmps;
  }
  const watts = model.selfDrawWatts || 0;
  if (!(watts > 0)) return 0;
  const v = typicalDeviceVoltage(model);
  if (!v) return 0;
  return Math.round((watts / v) * 1000) / 1000;
}
